'use client'

import { type CSSProperties, useEffect, useId, useRef } from 'react'
import { newBrowserId } from '@/lib/browser-id'
import { cn } from '@/lib/utils'
import { buildAuroraField, type AuroraField } from './aurora-field'
import { paintAurora, readAuroraPalette } from './aurora-webgl'
import { createVisualRandom, toVisualSeed } from './seeded-random'

type ÅBgType = 'aurora' | 'glow'

interface ÅBgProps {
  type?: ÅBgType
  base?: string
  mid?: string
  glow?: string
  intensity?: number
  scale?: number
  grain?: number
  stars?: number
  seed?: number
  className?: string
  style?: CSSProperties
}

interface AuroraStyle extends CSSProperties {
  '--aurora-base'?: string
  '--aurora-mid'?: string
  '--aurora-glow'?: string
}

interface BgStar {
  x: number
  y: number
  size: number
  alpha: number
}

const NOISE_TILE = 200
const NOISE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E\")"

function toAlphaColor(color: string, percent: number) {
  return `color-mix(in oklab, ${color} ${percent}%, transparent)`
}

function buildStars(count: number, seed: number): BgStar[] {
  const random = createVisualRandom(seed)
  return Array.from({ length: count }, () => ({
    x: random() * 100,
    y: random() * 100,
    size: 0.6 + random() * 1,
    alpha: 0.2 + random() * 0.55,
  }))
}

function drawAuroraCanvas(canvas: HTMLCanvasElement, field: AuroraField) {
  const bounds = canvas.getBoundingClientRect()
  const ratio = Math.min(window.devicePixelRatio || 1, 2)
  const width = Math.max(1, Math.round(bounds.width * ratio))
  const height = Math.max(1, Math.round(bounds.height * ratio))
  if (canvas.width !== width) canvas.width = width
  if (canvas.height !== height) canvas.height = height
  paintAurora(canvas, {
    field,
    palette: readAuroraPalette(canvas),
  })
}

function observeAuroraCanvas(canvas: HTMLCanvasElement, draw: () => void) {
  let frame = 0
  const schedule = () => {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(draw)
  }
  const resizeObserver = new ResizeObserver(schedule)
  const themeObserver = new MutationObserver(schedule)
  resizeObserver.observe(canvas)
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'style'],
  })
  schedule()
  return () => {
    cancelAnimationFrame(frame)
    resizeObserver.disconnect()
    themeObserver.disconnect()
  }
}

function AuroraBg({
  base,
  mid,
  glow,
  intensity = 0.82,
  scale = 1,
  className,
  style,
}: ÅBgProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const field = buildAuroraField({
      seed: newBrowserId(),
      intensity,
      scale,
    })
    return observeAuroraCanvas(canvas, () => drawAuroraCanvas(canvas, field))
  }, [base, glow, intensity, mid, scale])

  const auroraStyle: AuroraStyle = {
    ...style,
    '--aurora-base': base,
    '--aurora-mid': mid,
    '--aurora-glow': glow,
  }
  return (
    <canvas
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 size-full',
        className,
      )}
      data-bg
      data-bg-type="aurora"
      ref={canvasRef}
      style={auroraStyle}
    />
  )
}

function GlowBg({
  base = 'var(--highlight-base)',
  mid = 'var(--highlight-mid)',
  glow = 'var(--highlight-glow)',
  grain = 0.75,
  stars = 26,
  seed,
  className,
  style,
}: ÅBgProps) {
  const id = useId()
  const wash = [
    `radial-gradient(115% 95% at 50% 82%, ${toAlphaColor(mid, 72)}, transparent 70%)`,
    `radial-gradient(72% 56% at 80% 4%, ${toAlphaColor(mid, 28)}, transparent 62%)`,
  ].join(', ')
  const bloom = `radial-gradient(140% 112% at 50% 120%, ${glow}, transparent 72%)`
  const vignette = `radial-gradient(108% 84% at 50% 104%, transparent 32%, ${toAlphaColor(base, 88)} 100%)`
  const starLayers = buildStars(stars, seed ?? toVisualSeed(id))
    .map(
      (star) =>
        `radial-gradient(circle ${star.size.toFixed(2)}px at ${star.x.toFixed(2)}% ${star.y.toFixed(2)}%, rgb(255 255 255 / ${star.alpha.toFixed(2)}), transparent)`,
    )
    .join(', ')

  return (
    <div
      aria-hidden
      className={cn('pointer-events-none absolute inset-0 isolate', className)}
      data-bg
      data-bg-type="glow"
      style={{ backgroundColor: base, ...style }}
    >
      <div className="absolute inset-0" style={{ backgroundImage: wash }} />
      <div
        className="glow-bg-bloom"
        style={{
          backgroundImage: bloom,
          transform: 'scale(var(--bg-spread, 1))',
        }}
      />
      <div className="absolute inset-0" style={{ backgroundImage: vignette }} />
      {stars > 0 ? (
        <div
          className="absolute inset-0"
          style={{ backgroundImage: starLayers }}
        />
      ) : null}
      {grain > 0 ? (
        <div
          className="absolute inset-0 mix-blend-overlay"
          style={{
            backgroundImage: NOISE,
            backgroundSize: `${NOISE_TILE}px ${NOISE_TILE}px`,
            opacity: grain,
          }}
        />
      ) : null}
      <div className="absolute inset-0 shadow-[inset_0_1px_0_0_rgb(255_255_255/0.14)]" />
    </div>
  )
}

function Bg({ type = 'aurora', ...props }: ÅBgProps) {
  return type === 'glow' ? <GlowBg {...props} /> : <AuroraBg {...props} />
}

export { Bg as ÅBg }
export type { ÅBgProps, ÅBgType }
